import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import * as CmdTableService from '../CmdTable/CmdTableService';
import {
  FUEL_CARD_CREATED_AT_KEY,
  FUEL_CARD_CUSTOMER_ID_KEY,
  FUEL_CARD_DELETED_AT_KEY,
  FUEL_CARD_DETAIL_KEY,
  FUEL_CARD_QUERY_KEY,
  FUEL_CARD_TABLE_KEY,
  FUEL_CARD_TABLE_SETTINGS,
  TableSettingsKeys,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { TFuelCardRecords, TFuelCardResponse } from './interfaces';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import useSuppliers from './useFuelCards';
import Form from './Form';
// import { TABLE_FUEL_CARD } from '../../Services/TableService';
import PermissionsService, { PERMISSION_REFUELING_CARDS } from '../../Services/PermissionsService';
import { URL_FUEL_CARDS } from '../../router/constants';
// import { IValidationRule } from '../../Services/FormikValidatorService';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';

export default function FuelCards() {
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [pathname, navigate] = useLocation();
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const query = useUrlQueryParams();
  const [, params] = useRoute(`${URL_FUEL_CARDS}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(FUEL_CARD_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(FUEL_CARD_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(FUEL_CARD_TABLE_KEY)
  );
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useSuppliers();

  const handleClose = React.useCallback(() => {
    navigate(`${URL_FUEL_CARDS}`);
    setIsModalOpened(false);
  }, [navigate]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [FUEL_CARD_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  const handlePathData = (
    documentTitle: string,
    customerValue: string | null,
    modalOpened: boolean
  ) => {
    setCustomerId(customerValue ? Number(customerValue) : null);
    setDocumentTitle(documentTitle);
    setIsModalOpened(modalOpened);
  };

  React.useEffect(() => {
    if (pathname === URL_FUEL_CARDS) {
      const documentTitle = `${f({ id: 'menu.fuelCardsOverview' })} - ${f({
        id: 'menu.refuelings',
      })}`;
      handlePathData(documentTitle, null, false);
    } else if (pathname === `${URL_FUEL_CARDS}/add`) {
      const documentTitle = `${f({ id: 'table.add' })} - ${f({
        id: 'menu.fuelCardsOverview',
      })} - ${f({
        id: 'menu.refuelings',
      })}`;
      handlePathData(documentTitle, null, true);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({
        id: 'menu.fuelCardsOverview',
      })} - ${f({
        id: 'menu.refuelings',
      })}`;
      handlePathData(documentTitle, customerIdUrl, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, pathname, params, query]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = CmdTableService.arrayOrder(
      [...tempTableSettingOrder],
      [...tempOrder],
      'key'
    );

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await CmdTableService.getTableSettings(FUEL_CARD_TABLE_KEY);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['cmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_FUEL_CARDS}/add`);
  };

  return (
    <div>
      <div className="w-page">
        <div className="w-page-content">
          <div className="page-header">
            <h4>
              {f({ id: 'menu.refuelings' })} / {f({ id: 'menu.fuelCardsOverview' })}
              <OnlineHelp topic="fuel-cards" />
            </h4>
          </div>
          <div className="container-fluid">
            {/* <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} /> */}
            <section className="basic-box border-top-thin border-top-gray">
              <div
                className="basic-box__content"
                // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
              >
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {/* <div> */}
                    {PermissionsService.hasAccess(
                      PERMISSION_REFUELING_CARDS,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        // style="margin-right: 7px;"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={`${FUEL_CARD_QUERY_KEY}`}
                      // actionColumnEnabled={true}
                      tableName={FUEL_CARD_TABLE_SETTINGS}
                      sort={{ column: FUEL_CARD_CREATED_AT_KEY, direction: 'desc' }}
                      translationKeys={TableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={FUEL_CARD_TABLE_KEY} />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                <CmdTable<TFuelCardResponse, TFuelCardRecords>
                  name={FUEL_CARD_TABLE_KEY}
                  // fetchSize={fetchSize}
                  // queryKey={FUEL_CARD_QUERY_KEY}
                  stickyColumns={[FUEL_CARD_DETAIL_KEY, FUEL_CARD_DELETED_AT_KEY]}
                  lockedColumns={[FUEL_CARD_CUSTOMER_ID_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && (
              <div>
                {PermissionsService.hasAccess(
                  PERMISSION_REFUELING_CARDS,
                  params ? ['read', 'update'] : ['create'],
                  customerId
                ) && (
                  <Form
                    id={params ? Number(params.id) : null}
                    customerId={customerId}
                    updateTable={updateTable}
                    handleClose={handleClose}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
